import { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";
import { C, F, Label, Rule, Spinner } from "../ui";
import { fmtUSD } from "../../utils/ddp";

const ESTADOS = [
  { key: "confirmado",      label: "Confirmado",        color: "#60A5FA", desc: "Pedido recibido y confirmado." },
  { key: "en_produccion",   label: "En producción",     color: "#F59E0B", desc: "Fábrica en China produciendo tu pedido." },
  { key: "embarcado",       label: "Embarcado",         color: "#8B5CF6", desc: "Contenedor en tránsito marítimo." },
  { key: "en_aduana",       label: "En aduana",         color: "#EC4899", desc: "Mercadería en proceso de despacho aduanero." },
  { key: "listo_deposito",  label: "Listo en depósito", color: "#3D7A5F", desc: "Tu pedido está en nuestro depósito en CABA." },
  { key: "entregado",       label: "Entregado",         color: "#555",    desc: "Pedido entregado. ¡Gracias por elegir ORIGEN!" },
];

function TimelineComprador({ estadoActual }) {
  const idx = ESTADOS.findIndex(e => e.key === estadoActual);
  const estadoInfo = ESTADOS[idx];
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        {ESTADOS.map((e, i) => (
          <div key={e.key} style={{ display: "flex", alignItems: "center", flex: i < ESTADOS.length - 1 ? 1 : 0 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: i === idx ? 12 : 8, height: i === idx ? 12 : 8,
                borderRadius: "50%",
                background: i < idx ? e.color : i === idx ? e.color : C.border2,
                border: i === idx ? `2px solid ${e.color}` : "none",
                transition: "all .3s", flexShrink: 0,
              }} />
              <div style={{
                fontFamily: F.sans, fontSize: 7, letterSpacing: 1,
                color: i <= idx ? e.color : C.muted,
                textTransform: "uppercase", whiteSpace: "nowrap",
                fontWeight: i === idx ? 600 : 400,
              }}>{e.label}</div>
            </div>
            {i < ESTADOS.length - 1 && (
              <div style={{
                flex: 1, height: "0.5px", margin: "0 4px", marginBottom: 14,
                background: i < idx ? ESTADOS[i].color : C.border,
              }} />
            )}
          </div>
        ))}
      </div>
      {estadoInfo && (
        <div style={{ background: C.s2, border: `0.5px solid ${estadoInfo.color}`, padding: "10px 14px" }}>
          <div style={{ fontFamily: F.sans, fontSize: 11, color: estadoInfo.color }}>{estadoInfo.desc}</div>
        </div>
      )}
    </div>
  );
}

export default function CompradorPedidos() {
  const { user } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      const snap = await getDocs(
        query(collection(db, "pedidos"), orderBy("creadoEn", "desc"))
      );
      const todos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filtrar por usuario actual
      setPedidos(todos.filter(p => p.usuario_id === user.uid || p.cliente_email === user.email));
      setLoading(false);
    };
    load();
  }, [user]);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: F.serif, fontSize: 24, color: C.white, marginBottom: 4 }}>Mis compras</div>
        <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{pedidos.length} pedidos</div>
      </div>

      {pedidos.length === 0 ? (
        <div style={{ background: C.s1, border: `0.5px solid ${C.border}`, padding: 48, textAlign: "center" }}>
          <div style={{ fontFamily: F.serif, fontSize: 20, color: C.white, marginBottom: 8 }}>Sin pedidos aún</div>
          <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted }}>
            Explorá el catálogo y confirmá tu primer pedido.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5px", background: C.border }}>
          {pedidos.map(p => (
            <div key={p.id} style={{ background: C.s1 }}>
              {/* Header del pedido */}
              <div
                onClick={() => setSelected(selected?.id === p.id ? null : p)}
                style={{ padding: "18px 20px", cursor: "pointer", transition: "background .15s" }}
                onMouseEnter={e => e.currentTarget.style.background = C.s2}
                onMouseLeave={e => e.currentTarget.style.background = C.s1}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: F.serif, fontSize: 16, color: C.white, marginBottom: 4 }}>
                      {p.ciclo_nombre || "Pedido"}
                    </div>
                    <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>
                      {p.items?.length || 0} productos · {new Date(p.creadoEn?.toDate?.() || Date.now()).toLocaleDateString("es-AR")}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: F.mono, fontSize: 16, color: C.white }}>{fmtUSD(p.ddp_total)}</div>
                    <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>Total DDP</div>
                  </div>
                </div>

                {/* Mini timeline */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {ESTADOS.map((e, i) => {
                    const idx = ESTADOS.findIndex(x => x.key === p.estado);
                    return (
                      <div key={e.key} style={{ display: "flex", alignItems: "center", flex: i < ESTADOS.length - 1 ? 1 : 0 }}>
                        <div style={{
                          width: i === idx ? 8 : 6, height: i === idx ? 8 : 6,
                          borderRadius: "50%",
                          background: i <= idx ? e.color : C.border2,
                          flexShrink: 0,
                        }} />
                        {i < ESTADOS.length - 1 && (
                          <div style={{ flex: 1, height: "0.5px", background: i < idx ? e.color : C.border }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Detalle expandido */}
              {selected?.id === p.id && (
                <div style={{ padding: "0 20px 20px", borderTop: `0.5px solid ${C.border}` }}>
                  <div style={{ paddingTop: 20 }}>
                    <TimelineComprador estadoActual={p.estado} />
                  </div>

                  <Rule my={16} />

                  {/* Resumen financiero */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "0.5px", background: C.border, marginBottom: 16 }}>
                    {[
                      ["FOB base", fmtUSD(p.fob_total)],
                      ["Total DDP (USD)", fmtUSD(p.ddp_total)],
                      ["Volumen", `${(p.cbm_total||0).toFixed(3)} m³`],
                    ].map(([l,v]) => (
                      <div key={l} style={{ background: C.s2, padding: "12px 14px" }}>
                        <Label style={{ marginBottom: 4 }}>{l}</Label>
                        <div style={{ fontFamily: F.mono, fontSize: 14, color: C.white }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Items */}
                  <Label style={{ marginBottom: 10 }}>Productos</Label>
                  {(p.items || []).map((item, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `0.5px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontFamily: F.sans, fontSize: 12, color: C.off }}>{item.nombre}</div>
                        <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>{item.qty} × {item.unidad}</div>
                      </div>
                      <div style={{ fontFamily: F.mono, fontSize: 11, color: C.white }}>{fmtUSD((item.fob_venta || 0) * item.qty)}</div>
                    </div>
                  ))}

                  <div style={{ marginTop: 16, padding: "12px 14px", background: "#0A1A0A", border: `0.5px solid ${C.green}` }}>
                    <div style={{ fontFamily: F.sans, fontSize: 11, color: C.green, marginBottom: 4 }}>
                      ¿Necesitás más información sobre tu pedido?
                    </div>
                    <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>
                      Contactanos por WhatsApp o email y te actualizamos en detalle.
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
